import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { listInstanceAudit } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/**
 * Per-instance configuration audit (CT-4192). Returns the rows plus a reference
 * `now` captured off-render at load time, so the date-range cutoff and relative
 * timestamps stay lint-pure and consistent. Race/unmount-safe like useInstance.
 */
export function useInstanceAudit(instanceId) {
  const { t } = useTranslation('admin-studio');
  const [rows, setRows] = useState([]);
  const [now, setNow] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await listInstanceAudit(instanceId);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setRows(validateApiResponse(res, t('common.requestFailed')).body ?? []);
      setNow(Date.now());
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [instanceId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(() => ({ rows, now, isLoading, error }), [rows, now, isLoading, error]);
}
